// The dashboard's navigation: six sections in the sidebar, each a group of
// views shown as tabs; the glossary sits apart, behind the header's «?».
// `currentView` stays the one source of truth and the section is derived from
// it, so a view opened from elsewhere (the Events Stream jumping to the
// Session Inspector) lights up the right section.

import { effect, signal } from "@preact/signals";

import type { TranslationKey } from "@/i18n";
import { currentView, type ViewId } from "@/state/filters";

export type SectionId = "overview" | "conversations" | "usage" | "quality" | "cost" | "ops";

export interface Section {
  id: SectionId;
  label: TranslationKey;
  icon: string;
  views: readonly ViewId[];
}

export const SECTIONS: readonly Section[] = [
  { id: "overview", label: "section.overview", icon: "🩺", views: ["health", "kpi", "pulse"] },
  {
    id: "conversations",
    label: "section.conversations",
    icon: "💬",
    views: ["qna", "sessions", "events", "analyst", "insights"],
  },
  {
    id: "usage",
    label: "section.usage",
    icon: "📈",
    views: ["engagement", "peak", "talentgeo", "aggregates"],
  },
  {
    id: "quality",
    label: "section.quality",
    icon: "🛡️",
    views: ["quality", "signals", "questions"],
  },
  { id: "cost", label: "section.cost", icon: "💰", views: ["cost", "estimate", "partner"] },
  {
    id: "ops",
    label: "section.ops",
    icon: "🎛️",
    views: ["ops", "customers", "testpage", "config", "prompts"],
  },
];

/** The section a view belongs to; the glossary belongs to none. */
export function sectionOf(view: ViewId): Section | undefined {
  return SECTIONS.find((section) => section.views.includes(view));
}

/** The tab last shown in each section, however it was reached. */
export const lastViewBySection = signal<Partial<Record<SectionId, ViewId>>>({});

effect(() => {
  const view = currentView.value;
  const section = sectionOf(view);
  if (section) lastViewBySection.value = { ...lastViewBySection.peek(), [section.id]: view };
});

/** Opens a section at the tab last shown there, else at its first. */
export function openSection(id: SectionId): void {
  const section = SECTIONS.find((s) => s.id === id);
  if (!section) return;
  currentView.value = lastViewBySection.peek()[id] ?? section.views[0];
}
